import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";

// ログイン情報管理
import { useRecoilValue } from "recoil";
import { LoginUserAtom } from "../stores/LoginUserAtom";

// DB接続の際に使用
import axios from "axios";
import ConstList from "../utils/ConstList";

// ロードアイコン
import CircularProgress from "@mui/material/CircularProgress";

// 入力したデータの更新に使用する
import { Controller, useForm } from "react-hook-form";

// 日付の取得
import "dayjs/locale/ja";

type Props = {
  AddOpen: boolean;
  SetAddOpen: React.Dispatch<React.SetStateAction<boolean>>;
  ReViewPage: React.Dispatch<React.SetStateAction<boolean>>;
  leadSourceList?: any[];
};

type FormValues = {
  Company_name: string;
  Business_description: string;
  Company_telephone: string;
  Company_mail: string;
  Company_address: string;
  Company_website: string;
  lead_sources_id: number | "";
};

const colors = {
  bg: "#2D2C3A",
  panel: "#1F2530",
  panel2: "#252B3A",
  panel3: "#30364A",
  header: "#1F1D30",
  border: "rgba(217, 217, 217, 0.12)",
  text: "#F5F7FA",
  muted: "#AAB4C3",
  accent: "#C5E763",
  accentSoft: "rgba(197, 231, 99, 0.12)",
  accentBorder: "rgba(197, 231, 99, 0.38)",
  danger: "#ff8579",
  dangerSoft: "rgba(255, 133, 121, 0.12)",
};

const defaultValues: FormValues = {
  Company_name: "",
  Business_description: "",
  Company_telephone: "",
  Company_mail: "",
  Company_address: "",
  Company_website: "",
  lead_sources_id: "",
};

const inputSx = {
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.14)",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor: colors.panel2,
    color: colors.text,
    "&:hover": {
      backgroundColor: colors.panel3,
    },
    "&.Mui-focused": {
      backgroundColor: colors.panel3,
      boxShadow: "0 0 0 2px rgba(197,231,99,0.14)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.accent,
    },
  },
  "& .MuiInputBase-input": {
    color: colors.text,
  },
  "& .MuiInputBase-input::placeholder": {
    color: colors.muted,
    opacity: 1,
  },
  "& .MuiInputLabel-root": {
    color: colors.muted,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: colors.accent,
  },
  "& .MuiFormHelperText-root": {
    color: colors.muted,
  },
  "& .MuiFormHelperText-root.Mui-error": {
    color: colors.danger,
  },
};

const selectSx = {
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.14)",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor: colors.panel2,
    color: colors.text,
    "&:hover": {
      backgroundColor: colors.panel3,
    },
    "&.Mui-focused": {
      backgroundColor: colors.panel3,
      boxShadow: "0 0 0 2px rgba(197,231,99,0.14)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.accent,
    },
  },
  "& .MuiInputLabel-root": {
    color: colors.muted,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: colors.accent,
  },
  "& .MuiSelect-icon": {
    color: colors.muted,
  },
  "& .MuiSelect-select": {
    color: colors.text,
  },
  "& .MuiFormHelperText-root": {
    color: colors.muted,
  },
  "& .MuiFormHelperText-root.Mui-error": {
    color: colors.danger,
  },
};

const SectionTitle = ({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) => (
  <Stack direction="row" spacing={1.4} alignItems="center" sx={{ mb: 2 }}>
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 2,
        display: "grid",
        placeItems: "center",
        color: colors.accent,
        backgroundColor: colors.accentSoft,
        border: `1px solid ${colors.accentBorder}`,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>

    <Box>
      <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: 15 }}>
        {title}
      </Typography>
      <Typography sx={{ color: colors.muted, fontSize: 12.5, mt: 0.2 }}>
        {description}
      </Typography>
    </Box>
  </Stack>
);

const Pros_customer_Add = ({
  AddOpen,
  SetAddOpen,
  ReViewPage,
  leadSourceList = [],
}: Props) => {
  // ログイン - Atom
  const loginUserData = useRecoilValue(LoginUserAtom);

  // 画面の読み込みフラグ
  const [loading, setLoading] = useState<boolean>(true);

  // 保存処理中かどうかを管理
  const [isSaving, setIsSaving] = useState(false);

  // 登録完了のメッセージ表示状態
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // 工場の情報がすべて入っているもの
  const [MasterFactoryData] = useState<any[]>([]);

  // 重複エラー表示
  const [dupSnackbarOpen, setDupSnackbarOpen] = useState(false);
  const [dupMessage, setDupMessage] = useState<string>("");
  const [dupError, setDupError] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues,
  });

  // ###################################################################
  // #################### 画面を開いたとき ###########################
  // ###################################################################

  useEffect(() => {
    if (!AddOpen) return;

    setLoading(true);

    // 前回の登録内容を削除するために必要
    reset(defaultValues);

    setDupSnackbarOpen(false);
    setDupMessage("");
    setDupError(false);
    setIsSaving(false);

    const timer = setTimeout(() => {
      setLoading(false);
    }, 250);

    return () => clearTimeout(timer);
  }, [AddOpen, reset]);

  // ###################################################################
  // #################### 保存ボタン押したとき ###########################
  // ###################################################################

  const onSubmit = async (data: FormValues) => {
    // すでに保存処理中なら何もしない
    if (isSaving) return;

    // 全角・スペースを半角に変換
    const normalizeCompanyName = (s: string): string => {
      return (s ?? "")
        .trim()
        .replace(/[！-～]/g, (c) =>
          String.fromCharCode(c.charCodeAt(0) - 0xfee0)
        )
        .replace(/　/g, " ")
        .replace(/\s+/g, " ");
    };

    // 入力値の正規化
    const inputCompanyName = normalizeCompanyName(data.Company_name);

    // 空（trim後）を弾く（required はあるが念のため）
    if (!inputCompanyName) {
      setDupMessage("企業名を入力してください。");
      setDupSnackbarOpen(true);
      return;
    }

    // MasterFactoryData 内に同名があるかチェック
    const exists = MasterFactoryData.some((x) => {
      const name = normalizeCompanyName(x.Company_name);
      return name === inputCompanyName;
    });

    if (exists) {
      setDupError(true);
      setDupMessage(`「${inputCompanyName}」はすでに登録されています。`);
      setDupSnackbarOpen(true);
      return;
    }

    // 保存処理を開始
    setIsSaving(true);

    // データベースに問い合わせする際の配列定義
    const SaveArray = {
      Company_name: inputCompanyName,
      Business_description: data.Business_description,
      Company_telephone: data.Company_telephone,
      Company_mail: data.Company_mail,
      Company_address: data.Company_address,
      Company_website: data.Company_website,

      // 営業に至ったきっかけ
      lead_sources_id:
        data.lead_sources_id === "" ? null : Number(data.lead_sources_id),
    };

    try {
      const postData = {
        flag: "AddNewProsCustomersInfo",
        access_token: loginUserData.accessToken,
        algorithm: loginUserData.algorithm,
        SaveData: SaveArray,
        UserId: loginUserData.userId,
      };

      console.log("登録送信データ", postData);

      const response = await axios.post(
        ConstList.getApiUrl() + "EditProsCustomers.php",
        postData,
        ConstList.AXIOS_CONFIG
      );

      console.log("登録結果", response.data);

      // Auth0認証
      if (response.data.message === "Auth認証失敗") {
        alert("エラー：Auth0認証失敗");
        setIsSaving(false);
        return;
      }

      if (response.data.message === "メンテナンス中") {
        window.location.href = "./maintenance";
        return;
      }

      if (response.data.message === "登録成功") {
        reset(defaultValues);
        setOpenSnackbar(true);

        setTimeout(() => {
          setOpenSnackbar(false);
          ReViewPage(true);
          SetAddOpen(false);
          setIsSaving(false);
        }, 600);

        return;
      }

      setDupMessage(response.data.message || "保存に失敗しました。");
      setDupSnackbarOpen(true);
      setIsSaving(false);
    } catch (error) {
      alert("エラー：保存");
      console.log(error);
      setIsSaving(false);
    }
  };

  // ##################################################################
  // ################ キャンセルボタン押したとき ########################
  // ##################################################################

  const handleCancel = () => {
    if (isSaving) return;

    reset(defaultValues);
    SetAddOpen(false);
  };

  // ##################################################################
  // ######################## 画面表示 ############################
  // ##################################################################

  return (
    <Dialog
      open={AddOpen}
      fullWidth
      maxWidth="md"
      onClose={handleCancel}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          boxShadow: "0 22px 60px rgba(0,0,0,0.38)",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          backgroundColor: colors.header,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Box
          sx={{
            p: 2.5,
            background:
              "linear-gradient(135deg, rgba(197,231,99,0.12), rgba(31,29,48,0.96) 46%, rgba(31,37,48,1))",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 900, color: colors.text }}>
                潜在顧客 新規登録
              </Typography>

            </Box>

          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          maxHeight: "72vh",
          overflowY: "auto",
          p: { xs: 2, md: 2.5 },
          backgroundColor: colors.bg,
          borderColor: colors.border,
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 220,
              color: colors.text,
            }}
          >
            <CircularProgress sx={{ color: colors.accent }} />
            <Typography sx={{ ml: 2, color: colors.muted }}>
              読み込み中...
            </Typography>
          </Box>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Container maxWidth="md" disableGutters>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  backgroundColor: colors.panel,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <SectionTitle
                  icon={<BusinessRoundedIcon fontSize="small" />}
                  title="企業基本情報"
                  description="一覧・営業日報で参照する基本情報です。"
                />

                <Grid container spacing={2}>
                  {/* 会社名称 */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="企業名称"
                      error={dupError || !!errors.Company_name}
                      helperText={
                        dupError
                          ? "既に登録のある企業です"
                          : errors.Company_name?.message
                      }
                      sx={inputSx}
                      {...register("Company_name", {
                        required: "企業名は必須です",
                        onChange: () => setDupError(false),
                      })}
                    />
                  </Grid>

                  {/* 営業に至ったきっかけ */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="lead_sources_id"
                      control={control}
                      rules={{
                        required:
                          leadSourceList.length > 0
                            ? "営業に至ったきっかけは必須です"
                            : false,
                      }}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          error={!!errors.lead_sources_id}
                          sx={selectSx}
                        >
                          <InputLabel id="lead-sources-label">
                            営業に至ったきっかけ
                          </InputLabel>

                          <Select
                            labelId="lead-sources-label"
                            label="営業に至ったきっかけ"
                            value={field.value === "" ? "" : String(field.value)}
                            onChange={(e) => {
                              const value = String(e.target.value);
                              field.onChange(value === "" ? "" : Number(value));
                            }}
                            MenuProps={selectMenuProps}
                          >
                            <MenuItem value="">
                              <em>未選択</em>
                            </MenuItem>

                            {leadSourceList.map((item: any) => (
                              <MenuItem
                                key={item.lead_sources_id}
                                value={String(item.lead_sources_id)}
                              >
                                {item.lead_sources}
                              </MenuItem>
                            ))}
                          </Select>

                          <FormHelperText>
                            {errors.lead_sources_id?.message}
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  {/* 業種 */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="業種情報"
                      error={dupError || !!errors.Business_description}
                      helperText={
                        dupError
                          ? "その記述方法では登録できません"
                          : errors.Business_description?.message
                      }
                      sx={inputSx}
                      {...register("Business_description", {
                        required: "業種の入力は必須です",
                        onChange: () => setDupError(false),
                      })}
                    />
                  </Grid>

                  {/* 住所 */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="住所"
                      error={dupError || !!errors.Company_address}
                      helperText={
                        dupError
                          ? "同じ住所は登録できません"
                          : errors.Company_address?.message
                      }
                      sx={inputSx}
                      {...register("Company_address", {
                        required: "住所の登録は必須です",
                        onChange: () => setDupError(false),
                      })}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  backgroundColor: colors.panel,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <SectionTitle
                  icon={<PhoneRoundedIcon fontSize="small" />}
                  title="連絡先・Web情報"
                  description="電話・メール・URLは営業時の確認先として使用します。"
                />

                <Grid container spacing={2}>
                  {/* 電話番号 */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="電話番号"
                      error={dupError || !!errors.Company_telephone}
                      helperText={
                        dupError
                          ? "同じ電話番号は登録できません"
                          : errors.Company_telephone?.message
                      }
                      sx={inputSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneRoundedIcon sx={{ color: colors.muted }} />
                          </InputAdornment>
                        ),
                      }}
                      {...register("Company_telephone", {
                        required: "電話番号は必須です",
                        onChange: () => setDupError(false),
                      })}
                    />
                  </Grid>

                  {/* メール */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="メールアドレス"
                      error={dupError || !!errors.Company_mail}
                      helperText={
                        dupError
                          ? "同じメールアドレスは登録できません"
                          : errors.Company_mail?.message
                      }
                      sx={inputSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MailOutlineRoundedIcon sx={{ color: colors.muted }} />
                          </InputAdornment>
                        ),
                      }}
                      {...register("Company_mail", {
                        required: "メールアドレスは必須です",
                        onChange: () => setDupError(false),
                      })}
                    />
                  </Grid>

                  {/* サイトURL */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="サイトURL"
                      error={dupError || !!errors.Company_website}
                      helperText={
                        dupError
                          ? "同じURLは登録できません"
                          : errors.Company_website?.message ||
                            "一覧のURLアイコンからWebサイトを開けるようになります。"
                      }
                      sx={inputSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkRoundedIcon sx={{ color: colors.muted }} />
                          </InputAdornment>
                        ),
                      }}
                      {...register("Company_website", {
                        required: "サイトURLは必須です",
                        onChange: () => setDupError(false),
                      })}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Container>
          </form>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          backgroundColor: colors.header,
          borderTop: `1px solid ${colors.border}`,
        }}
      >
        <Button
          onClick={handleCancel}
          disabled={isSaving}
          variant="outlined"
          startIcon={<CloseRoundedIcon />}
          sx={{
            color: colors.text,
            borderColor: "rgba(255,255,255,0.18)",
            fontWeight: 800,
            "&:hover": {
              borderColor: colors.accent,
              backgroundColor: colors.accentSoft,
            },
          }}
        >
          キャンセル
        </Button>

        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={isSaving || loading}
          variant="contained"
          startIcon={
            isSaving ? undefined : <SaveRoundedIcon sx={{ color: "#111827" }} />
          }
          sx={{
            backgroundColor: colors.accent,
            color: "#111827",
            fontWeight: 900,
            "&:hover": {
              backgroundColor: "#D7F580",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(197,231,99,0.32)",
              color: "rgba(17,24,39,0.55)",
            },
          }}
        >
          {isSaving ? (
            <CircularProgress size={24} sx={{ color: "#111827" }} />
          ) : (
            "新規登録"
          )}
        </Button>
      </DialogActions>

      {/* 登録完了メッセージ */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={1000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert severity="success">新規登録が完了しました。</Alert>
      </Snackbar>

      {/* 重複確認メッセージ */}
      <Snackbar
        open={dupSnackbarOpen}
        autoHideDuration={2400}
        onClose={() => setDupSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="warning" onClose={() => setDupSnackbarOpen(false)}>
          {dupMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 48 * 5 + 8,
      overflowY: "auto",
      borderRadius: 2,
      mt: 0.5,
      backgroundColor: colors.panel,
      color: colors.text,
      border: `1px solid ${colors.border}`,
      "& .MuiMenuItem-root": {
        color: colors.text,
      },
      "& .MuiMenuItem-root:hover": {
        backgroundColor: colors.accentSoft,
      },
      "& .Mui-selected": {
        backgroundColor: `${colors.accentSoft} !important`,
        color: colors.accent,
      },
    },
  },
};

export default Pros_customer_Add;
